import { InputForm } from 'components/InputForm';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ApplicationState } from 'store/store.inteface';
import { getBankList } from 'store/modules/openClaim/actions';
import { Tooltip, Checkbox } from '@grupoboticario/flora-react';
import { ReactComponent as QuestionMark } from 'assets/images/questionMark.svg';
import { ReactSelectFloraController } from 'components/ReactSelectFlora';
import { schema, schemaDraft } from './schema';
import { Label, ColInput, RowInput } from '../sharedStyles';
import { Cards } from './cards';
import ButtonContainer from '../components/ButtonContainer';
import { Wrapper, Warning, ConfirmData } from './styles';
import ModalSaveDraft from '../components/ModalSaveDraft';
import useModalSaveDraft from '../useModalSaveDraft';
import mockBank from './mockBank.json';

export const Step3 = (): JSX.Element => {
  const dispatch = useDispatch();

  const {
    openModal,
    setOpenModal,
    stateModal,
    setStateModal,
    onClickForward,
    saveDraftAndLeave,
    onClickCancel,
    onError,
  } = useModalSaveDraft('step3');

  const [optionBank, setOptionBank] = useState([]);

  const [stateSchema, setStateSchema] = useState<any>(schema);

  const banklist = useSelector(
    (state: ApplicationState) => state.openClaim.banklist,
  );

  const { stores } = useSelector(
    (state: ApplicationState) => state.storesContext,
  );

  const stateStep3 = useSelector(
    (state: ApplicationState) => state.openClaim.step.step3,
  );

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(stateSchema),
    defaultValues: stateStep3,
  });

  const watchFields = useWatch({
    control,
    name: [
      'accountable',
      'email',
      'cellphone',
      'bank',
      'agency',
      'account',
      'confirmData',
    ],
  });

  useEffect(() => {
    if (openModal) {
      setStateSchema(schemaDraft);
    }
  }, [openModal]);

  useEffect(() => {
    if (!banklist && stores.length > 0) {
      dispatch(getBankList());
    } else if (optionBank.length === 0) {
      const chosenBankList = banklist.response ? banklist : mockBank; // se não houver response, pegar do mock
      const newOptionsBank = chosenBankList.response.map((banco: any) => {
        return {
          label: `${banco.NumeroCodigo} - ${banco.NomeReduzido}`,
          value: banco.ISPB,
          data: banco,
        };
      });
      setOptionBank(newOptionsBank);
    }
  }, [banklist, optionBank, dispatch, stores]);

  return (
    <Wrapper>
      <Cards />
      <form onSubmit={handleSubmit(onClickForward)}>
        <Warning>
          Caso tenha ocorrido alguma mudança de endereço do local da ocorrência
          acima, notifique no campo abaixo todas as alterações.
        </Warning>
        <RowInput>
          <ColInput>
            <InputForm
              label="Observações:"
              id="1"
              control={control}
              name="comments"
              type="text"
              placeholder="Logradouro, Número, CEP"
              maxLength={100}
            />
          </ColInput>
        </RowInput>
        <Label className="only-warning">
          <span>
            <span>*</span> Campos obrigatórios para abertura de sinistro
          </span>
        </Label>
        <RowInput>
          <ColInput>
            <InputForm
              label="Responsável pelo Sinistro:"
              id="2"
              control={control}
              name="accountable"
              type="text"
              placeholder=""
              maxLength={100}
              required
            />
          </ColInput>
        </RowInput>
        <RowInput>
          <ColInput>
            <InputForm
              label="Email:"
              id="3"
              control={control}
              name="email"
              type="email"
              placeholder=""
              required
            />
          </ColInput>
          <ColInput>
            <InputForm
              label="Celular:"
              id="4"
              control={control}
              mask="(xx) xxxxx-xxxx"
              name="cellphone"
              type="text"
              placeholder=""
              maxLength={15}
              required
            />
          </ColInput>
          <ColInput>
            <InputForm
              label="Tel. Comercial:"
              id="5"
              control={control}
              mask="(xx) xxxx-xxxx"
              name="businessphone"
              type="text"
              placeholder=""
              maxLength={14}
            />
          </ColInput>
        </RowInput>
        <Label>
          Preencha e confira os dados bancários{' '}
          <span>Não implica em reconhecimento da cobertura</span>
        </Label>
        <RowInput>
          <Tooltip
            icon={<QuestionMark />}
            align="center"
            direction="top"
            text="Digite nome ou número do banco"
          >
            <ColInput>
              <ReactSelectFloraController
                label="Banco:"
                options={optionBank}
                id="5"
                control={control}
                name="bank"
                required
                disabled={optionBank.length <= 0}
              />
            </ColInput>
          </Tooltip>
          <Tooltip
            icon={<QuestionMark />}
            align="center"
            direction="top"
            text="Digite apenas números incluindo o dígito da agência"
          >
            <ColInput>
              <InputForm
                label="Agência:"
                id="7"
                control={control}
                mask="xxxxx"
                maxLength={5}
                name="agency"
                type="text"
                placeholder=""
                required
              />
            </ColInput>
          </Tooltip>

          <Tooltip
            icon={<QuestionMark />}
            align="center"
            direction="top"
            text="Digite apenas números incluindo o dígito da conta (corrente/poupança)"
          >
            <ColInput>
              <InputForm
                label="Conta:"
                id="8"
                control={control}
                mask="xxxxxxxxx"
                maxLength={9}
                name="account"
                type="text"
                placeholder=""
                required
              />
            </ColInput>
          </Tooltip>
        </RowInput>
        <ConfirmData>
          <Controller
            name="confirmData"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Checkbox
                id="checkbox"
                {...field}
                required
                checked={field.value}
                onClick={() => field.onChange(!field.value)}
              />
            )}
          />
          Concordo que todos os dados acima estão corretos
        </ConfirmData>
        <ButtonContainer
          onClickForward={handleSubmit(onClickForward)}
          onClickCancel={onClickCancel}
          disabled={!Object.values(watchFields).every(v => v)}
        />
      </form>
      <ModalSaveDraft
        stateModal={stateModal}
        setStateModal={setStateModal}
        isOpen={openModal}
        setIsOpen={setOpenModal}
        handleSubmit={handleSubmit(saveDraftAndLeave, onError)}
      />
    </Wrapper>
  );
};
